import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import Button from 'components/Button'
import ConfirmModal from 'components/ConfirmModal'
import StyledCheckbox from 'components/StyledCheckbox'

const MIN_SELECTED = 3
const MAX_SELECTED = 10

const formatDate = iso => {
  const m = moment(iso)
  return m.isValid() ? m.format('MMM D, YYYY · h:mm A') : '—'
}

const formatDuration = minutes => {
  if (minutes == null) return null
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rem = minutes % 60
  return rem ? `${hours}h ${rem}m` : `${hours}h`
}

const formatParticipants = participants => {
  if (
    !Array.isArray(participants) ||
    participants.length === 0
  ) {
    return null
  }
  const labels = participants
    .map(p => (typeof p === 'string' ? p : p && p.email))
    .filter(Boolean)
  if (labels.length === 0) return null
  if (labels.length <= 3) return labels.join(', ')
  return `${labels.slice(0, 3).join(', ')} +${labels.length - 3}`
}

const TranscriptPicker = ({
  transcripts,
  isLoading,
  error,
  onRetry,
  onGenerate,
  isGenerating,
  generateError,
  generateDisabledReason,
  providerName
}) => {
  const [selectedIds, setSelectedIds] = useState([])
  const [showConfirm, setShowConfirm] = useState(false)

  const openConfirm = () => {
    if (!onGenerate) return
    setShowConfirm(true)
  }
  const closeConfirm = () => setShowConfirm(false)
  const confirmGenerate = () => {
    onGenerate(selectedIds)
  }

  const sorted = useMemo(() => {
    return [...transcripts].sort((a, b) => {
      const da = new Date(a.date).getTime() || 0
      const db = new Date(b.date).getTime() || 0
      return db - da
    })
  }, [transcripts])

  const atMax = selectedIds.length >= MAX_SELECTED

  const toggle = id => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id)
      if (prev.length >= MAX_SELECTED) return prev
      return [...prev, id]
    })
  }

  const selectAll = () => {
    // Take the first MAX_SELECTED items in sorted order (most recent).
    setSelectedIds(sorted.slice(0, MAX_SELECTED).map(t => t.id))
  }
  const clearAll = () => {
    setSelectedIds([])
  }
  const allSelected =
    sorted.length > 0 &&
    sorted
      .slice(0, Math.min(sorted.length, MAX_SELECTED))
      .every(t => selectedIds.includes(t.id))

  const belowMinimum = selectedIds.length < MIN_SELECTED

  if (isLoading) {
    return (
      <div className="p-20 border rounded">
        <p className="mb-0">Loading your meetings…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-20 border rounded">
        <p className="text-danger mb-10">
          {error.message ||
            "Couldn't load your meetings. Please try again."}
        </p>
        {onRetry && (
          <Button
            variant="secondary"
            smallPadding
            onClick={onRetry}
          >
            Try again
          </Button>
        )}
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="p-20 border rounded bg-gray-light">
        <p className="font-weight-bold mb-5">
          No meetings found
        </p>
        <p className="text-gray-dark mb-0">
          We didn&apos;t find any recent meetings in your
          {providerName ? ` ${providerName}` : ' connected'}{' '}
          account. Once you have meetings recorded, they&apos;ll
          show up here.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-15">
        <p className="mb-0 text-gray-dark">
          {selectedIds.length} of {sorted.length} selected
          {belowMinimum && ` · pick at least ${MIN_SELECTED}`}
          {atMax && ` · max ${MAX_SELECTED} reached`}
        </p>
        <div>
          {allSelected ? (
            <button
              type="button"
              onClick={clearAll}
              className="p-0"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#3F8CFF',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          ) : (
            <button
              type="button"
              onClick={selectAll}
              className="p-0"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#3F8CFF',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {sorted.length > MAX_SELECTED
                ? `Select first ${MAX_SELECTED}`
                : 'Select all'}
            </button>
          )}
        </div>
      </div>

      <div
        className="border rounded mb-20"
        style={{
          maxHeight: 'calc(100vh - 380px)',
          minHeight: 160,
          overflowY: 'auto'
        }}
      >
        {sorted.map((t, idx) => {
          const isLast = idx === sorted.length - 1
          const isSelected = selectedIds.includes(t.id)
          const isDisabled = !isSelected && atMax
          const duration = formatDuration(t.durationMinutes)
          const participants = formatParticipants(t.participants)
          const handleRowClick = () => {
            if (isDisabled) return
            toggle(t.id)
          }
          return (
            <div
              key={t.id}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              onClick={handleRowClick}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleRowClick()
                }
              }}
              className={`p-15 ${isLast ? '' : 'border-bottom'}`}
              style={{
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'default' : 'pointer',
                backgroundColor: isSelected
                  ? '#F0F6FF'
                  : 'transparent',
                transition: 'background-color 0.15s ease'
              }}
            >
              <div className="d-flex align-items-start">
                <div
                  className="mr-15"
                  style={{
                    paddingTop: 2,
                    pointerEvents: 'none'
                  }}
                >
                  {/*
                    Visual-only — the row owns the click. Without
                    pointer-events:none, clicks on the StyledCheckbox's
                    pseudo-element either get absorbed by intermediate
                    wrappers or fire the input onChange while the row also
                    handles the bubble, double-toggling to a no-op.
                  */}
                  <StyledCheckbox
                    controlId={`transcript-${t.id}`}
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => {}}
                  />
                </div>
                <div className="flex-grow-1">
                  <p
                    className="font-weight-bold mb-0"
                    style={{ fontSize: 15 }}
                  >
                    {t.title || 'Untitled meeting'}
                  </p>
                  <p
                    className="text-gray-dark mb-0"
                    style={{ fontSize: 13 }}
                  >
                    {formatDate(t.date)}
                    {duration ? ` · ${duration}` : ''}
                  </p>
                  {participants && (
                    <p
                      className="text-gray-dark mb-0"
                      style={{ fontSize: 12 }}
                    >
                      {participants}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="d-flex align-items-center">
        <Button
          variant="secondary"
          smallPadding
          onClick={openConfirm}
          disabled={
            belowMinimum ||
            isGenerating ||
            !onGenerate ||
            Boolean(generateDisabledReason)
          }
        >
          {isGenerating ? 'Starting…' : 'Generate profile'}
        </Button>
        {generateDisabledReason && (
          <span className="text-gray-dark ml-15">
            {generateDisabledReason}
          </span>
        )}
      </div>

      {generateError && (
        <p className="text-danger mt-15 mb-0">
          {generateError.message ||
            "Couldn't start generation. Please try again."}
        </p>
      )}

      <ConfirmModal
        show={showConfirm}
        toggleModal={closeConfirm}
        title={`Generate profile from ${selectedIds.length} ${
          selectedIds.length === 1 ? 'meeting' : 'meetings'
        }?`}
        description={`Profile generation takes about 10 minutes per meeting selected — roughly ${
          selectedIds.length * 10
        } minutes for the ${selectedIds.length} ${
          selectedIds.length === 1 ? 'meeting' : 'meetings'
        } you picked. Each transcript is analyzed for 10 leadership competencies. You can close this tab and come back later — we’ll save your profile when it’s done.`}
        confirmText="Generate"
        onConfirm={confirmGenerate}
      />
    </div>
  )
}

TranscriptPicker.propTypes = {
  transcripts: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onRetry: PropTypes.func,
  onGenerate: PropTypes.func,
  isGenerating: PropTypes.bool,
  generateError: PropTypes.object,
  generateDisabledReason: PropTypes.string,
  providerName: PropTypes.string
}

export default TranscriptPicker
